import { useEffect, useRef, useCallback } from "react";
import { useAuth } from "../context/AuthContext";

type EntregaPainelEventType =
  | "entrega.created"
  | "entrega.updated"
  | "entrega.finalized"
  | "entrega.deleted"
  | "filaEntrega.created"
  | "filaEntrega.updated"
  | "entregador.chamado";

export const useEntregaPainelEvents = (
  onEvent: () => void,
  lojaId?: number | null,
  options?: {
    onAvailabilityChange?: (available: boolean) => void;
  }
) => {
  const { token } = useAuth();
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;
  const reconnectDelay = 3000;
  const onAvailabilityChangeRef = useRef(options?.onAvailabilityChange);
  onAvailabilityChangeRef.current = options?.onAvailabilityChange;

  const notifyAvailability = useCallback((available: boolean) => {
    onAvailabilityChangeRef.current?.(available);
  }, []);

  const connect = useCallback(() => {
    if (!token) return;

    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3001";
    const params = new URLSearchParams();
    if (lojaId) {
      params.append("lojaId", lojaId.toString());
    }
    params.append("token", token);
    const url = `${apiUrl}/events/ranking/entrega?${params.toString()}`;

    const eventSource = new EventSource(url, {
      withCredentials: false,
    });

    eventSource.onopen = () => {
      reconnectAttempts.current = 0;
      notifyAvailability(true);
    };

    eventSource.addEventListener("connected", () => {
      reconnectAttempts.current = 0;
      notifyAvailability(true);
      onEvent();
    });

    const eventTypes: EntregaPainelEventType[] = [
      "entrega.created",
      "entrega.updated",
      "entrega.finalized",
      "entrega.deleted",
      "filaEntrega.created",
      "filaEntrega.updated",
      "entregador.chamado",
    ];

    eventTypes.forEach((eventType) => {
      eventSource.addEventListener(eventType, () => {
        onEvent();
      });
    });

    eventSource.onerror = (error) => {
      console.error("SSE error for entrega (painel):", error);
      eventSource.close();

      if (reconnectAttempts.current < maxReconnectAttempts) {
        reconnectAttempts.current += 1;
        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, reconnectDelay * reconnectAttempts.current);
      } else {
        notifyAvailability(false);
      }
    };

    eventSourceRef.current = eventSource;
  }, [token, lojaId, onEvent, notifyAvailability]);

  useEffect(() => {
    connect();

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };
  }, [connect]);
};
